import { DownOutlined } from "@ant-design/icons";
import { Button, Dropdown, MenuProps, notification, Modal } from "antd";
import { message } from "utils/ciq-message";
import CiqAgSearch from "components/ciq-ag-search";
import { CiqIconBox } from "components/svg-icons";
import { SuccessMessages } from "constants/index";

import { ProjectContext } from "context/ProjectProvider";
import {
  FilterChipComponent,
  FilterChipComponentRef
} from "entity-app/shared-components/log-render-components/filter-chip-component";
import { exportEntityLogAsCSV } from "entity-app/utils/export-entity-log-as-csv";
import {
  exportEntityLogAsExcel,
  sanitizeSheetName
} from "components/excel-ag-grid-export/export-entity-log-as-excel";
import { ExcelExportOptions } from "components/excel-ag-grid-export/excel-export-option";
import ExportOptionsPopover, {
  ExportFormatOption,
  ExportSelection
} from "components/export-options-popover";

import { useCallback, useContext, useMemo } from "react";
import { AgGridReact } from "ag-grid-react";
import { getGridStatusBudge } from "components/ciq-log-page-header/grid-status-budge";
import PrintViewValidationModal from "pages/print-view-layout/Print-View-Validation-Modal";
import { DateUtils } from "utils/dateutils";
import useAuditEventLog from "entity-app/utils/export-audit-log";
import SubmittalUntrackedsTypeMenu from "./submittal-untrackeds-type-menu";

type Prop = {
  selectedRows: any[] | null;
  onOpenMoveTrackedModal: (submittals: any[]) => void;
  gridRef: React.RefObject<AgGridReact<any>>;
  filterChipRef: React.RefObject<FilterChipComponentRef>;
  pageData: any;
  statsData:
    | {
        displayCount: number;
        filterName: undefined | string;
      }
    | undefined;
};

export default function SubmittalUntrackedHeaderComponent({
  selectedRows,
  onOpenMoveTrackedModal,
  gridRef,
  pageData,
  filterChipRef,
  statsData
}: Prop) {
  const { projectDetails, gqlClientForProject, isProjectArchived } =
    useContext(ProjectContext);
  const cannotHaveTrackedPermission = !!isProjectArchived;
  const { saveAuditLogEvent } = useAuditEventLog(gqlClientForProject);

  const [, contextHolder] = notification.useNotification();

  const isPrintViewEnabled = false; // isFeatureFlagEnabled("PRINT_VIEW");
  const isExcelExportEnabled = false; // isFeatureFlagEnabled("EXCEL_EXPORT");

  const handleDeleteSubmittal = useCallback(
    async (submittalId: string[]) => {
      const deletedSubmittalsRes = await pageData.mutationDeleteSubmittal({
        variables: {
          feature_instance_ids: submittalId,
          feature_type_id: 1
        }
      });
      if (deletedSubmittalsRes.data) {
        message.success(SuccessMessages.deleteSuccess);
        pageData.fetchLogData();
      }
      if (deletedSubmittalsRes.errors) {
        console.error(deletedSubmittalsRes.errors);
        message.error("Failed to delete Submittals.");
      }
    },
    [pageData]
  );

  const deleteSubmittals = useCallback(() => {
    // Count selected rows that have not_found_in_source === true
    const submittalsToDelete =
      selectedRows
        ?.filter((row: any) => row?.not_found_in_source === true)
        ?.map((row: any) => row.id) || [];
    const presentSubmittalsCount =
      (selectedRows?.length || 0) - (submittalsToDelete?.length || 0);
    Modal.confirm({
      title: "Are you sure you want to delete the selected submittal(s)?",
      content:
        presentSubmittalsCount > 0
          ? `${presentSubmittalsCount} of the ${selectedRows?.length} selected submittals can't be deleted because they are still available in source system.`
          : null,
      okText: "Delete",
      cancelText: "Cancel",
      okButtonProps: {
        disabled: submittalsToDelete.length === 0
      },
      async onOk() {
        if (handleDeleteSubmittal && submittalsToDelete.length > 0) {
          handleDeleteSubmittal(submittalsToDelete);
        }
      }
    });
    return;
  }, [handleDeleteSubmittal, selectedRows]);

  const bulkEditActionMenu: MenuProps = useMemo(() => {
    return {
      items: [
        {
          type: "group",
          key: "bulk-edit",
          label: "Bulk Edit",
          children: [
            {
              key: "tracked",
              onClick: () => {
                if (!selectedRows?.length) return;
                onOpenMoveTrackedModal(selectedRows);
              },
              label: "Track Submittals"
            }
          ]
        }
      ]
    };
  }, [onOpenMoveTrackedModal, selectedRows]);

  const onOpenPrintView = useCallback(() => {
    if (!gridRef.current || !projectDetails) {
      message.error("Unable to open print view. Grid data is not available.");
      return;
    }
  }, [gridRef, projectDetails]);

  const bulkEditButton = useMemo(() => {
    if (!selectedRows) return null;
    const selectedCount = selectedRows.length;
    if (selectedCount < 1) return null;
    return (
      <div
        className="bg-ciq-gray-100 bg-opacity-70 py-1 px-4 flex items-center space-x-2 rounded-md"
        key="MaterialLogPage_BulkEdit"
      >
        <div className="py-1 whitespace-nowrap" key="MaterialLogPage_Selected">
          {selectedRows?.length} Selected
        </div>
        {selectedCount > 1 && (
          <Dropdown
            menu={bulkEditActionMenu}
            key="MaterialLogPage_bulkEdit"
            disabled={cannotHaveTrackedPermission}
          >
            <Button
              disabled={cannotHaveTrackedPermission}
              title={cannotHaveTrackedPermission ? undefined : "Actions"}
            >
              Actions <DownOutlined />
            </Button>
          </Dropdown>
        )}
        <Button
          key="MaterialLogPage_Delete"
          onClick={deleteSubmittals}
          size="middle"
          className="bottomBarBtn"
          title={cannotHaveTrackedPermission ? undefined : "Delete"}
          disabled={cannotHaveTrackedPermission}
        >
          Delete
        </Button>
      </div>
    );
  }, [
    bulkEditActionMenu,
    cannotHaveTrackedPermission,
    deleteSubmittals,
    selectedRows
  ]);

  const headerItems = useMemo(() => {
    const menus = [] as JSX.Element[];

    menus.push(
      <FilterChipComponent
        key="SubmittalLogUntracked_FilterChip"
        gridRef={gridRef}
        ref={filterChipRef}
      />
    );
    if (bulkEditButton) menus.push(bulkEditButton);
    menus.push(
      <CiqAgSearch key="SubmittalLogUntracked_search" gridRef={gridRef} />
    );

    const onExportCsv = () => {
      if (projectDetails && gridRef.current) {
        const filename = `Submittal_Log_Untracked_${projectDetails.name}.csv`;
        exportEntityLogAsCSV({
          filename,
          gridRef: {
            api: gridRef.current!.api
          },
          includedColumns: ["description"],
          excludedColumns: ["action"],
          skipRowGroups: true
        });

        saveAuditLogEvent({
          eventType: "SUBMITTAL_LOG_EXPORT_CSV",
          featureType: "SUBMITTALS",
          filename
        });
      }
    };

    const onExportExcel = (options: ExcelExportOptions) => {
      if (!gridRef.current || !projectDetails) return;
      const filename: string = `Submittal_not_tracked_log_${sanitizeSheetName(projectDetails.name)}.xlsx`;
      exportEntityLogAsExcel({
        projectDetails,
        gridRef,
        filename,
        prependText: `${
          projectDetails.name
        } - Submittal not tracked Log - Exported from ConstructivIQ on ${DateUtils.dateTimeObj().format(
          `M/D [at] h:mma [${DateUtils.dateTimeObj().format("z")}]`
        )}`,
        includedColumns: ["description"],
        excludedColumns: ["_milestones", "_durations", "action"],
        sortPosition: [
          {
            colId: "released_early_msg",
            dependentColId: "submittal-untracked_released_early",
            position: "after"
          }
        ],
        options
      });

      saveAuditLogEvent({
        eventType: "SUBMITTAL_LOG_EXPORT_EXCEL",
        featureType: "SUBMITTALS",
        filename
      });
    };

    const exportOptions: ExportFormatOption[] = [
      {
        key: "export_csv",
        title: "CSV",
        testId: "submittal-untracked-header-export-csv"
      }
    ];
    if (isExcelExportEnabled) {
      exportOptions.push({
        key: "export_excel",
        title: "Excel (.xlsx)",
        isExcel: true,
        disabled: !pageData.entityList?.length,
        testId: "submittal-untracked-header-export-excel"
      });
    }
    if (isPrintViewEnabled) {
      exportOptions.push({
        key: "print_view",
        title: "PDF/Print",
        testId: "submittal-untracked-header-print-view"
      });
    }

    const onProceedExport = ({ key, excelOptions }: ExportSelection) => {
      if (key === "export_excel") {
        if (excelOptions) onExportExcel(excelOptions);
        return;
      }
      if (key === "print_view") {
        if (gridRef.current)
          PrintViewValidationModal({
            gridApi: gridRef.current.api,
            onProceed: onOpenPrintView,
            skipColumnIds: ["action", "ag-Grid-SelectionColumn"]
          });
        return;
      }
      onExportCsv();
    };

    menus.push(
      <ExportOptionsPopover
        key="SubmittalLogUntracked_export"
        options={exportOptions}
        onProceed={onProceedExport}
        excelTableName={projectDetails?.name || "Project"}
        showIncludeComments={false}
        disabled={!pageData.entityList || !!isProjectArchived}
        buttonType="default"
        buttonTestId="submittal-untracked-header-export"
      />
    );
    return menus;
  }, [
    gridRef,
    filterChipRef,
    bulkEditButton,
    isExcelExportEnabled,
    isPrintViewEnabled,
    projectDetails,
    saveAuditLogEvent,
    pageData.entityList,
    isProjectArchived,
    onOpenPrintView
  ]);

  const statusBudge = useMemo(
    () =>
      getGridStatusBudge(
        {
          title: "Submittals not tracked",
          totalCount: pageData.entityList?.length,
          filterStats: statsData
        },
        () => {}
      ),
    [pageData.entityList?.length, statsData]
  );

  const submittalUntrackedsTitleDropdown = (
    <div
      className="flex items-center justify-start cursor-pointer"
      data-testid="submittal-untracked-header-title"
    >
      <CiqIconBox feature="SUBMITTALS_UNTRACKED" size={42} padding={0} />
      <div className="flex flex-col pl-3">
        <Dropdown
          menu={SubmittalUntrackedsTypeMenu()}
          trigger={["hover"]}
          placement="bottomLeft"
        >
          <div className="flex items-center gap-2">
            <span className="text-black text-xl font-semibold">
              Submittals Not Tracked
            </span>

            <DownOutlined className="text-xs self-center mt-0.5" />
          </div>
        </Dropdown>
      </div>
      {statusBudge && (
        <div
          className="flex shrink-0 ml-2"
          data-testid="submittal-untracked-header-stats"
        >
          {statusBudge}
        </div>
      )}
    </div>
  );

  return (
    <div
      className="w-full px-6 py-4 flex justify-between items-center"
      data-testid="submittal-untracked-header-items-container"
    >
      <div className="flex space-x-2 items-center text-black">
        {submittalUntrackedsTitleDropdown}
      </div>

      <div
        className="flex space-x-2 items-center"
        data-testid="submittal-untracked-header-actions"
      >
        {headerItems}
      </div>

      {contextHolder}
    </div>
  );
}
